import { MediaModel as MediaApi } from '@/http/models/media/Media'
import { useMutation } from '@tanstack/react-query'
import { FileType } from 'lucide-react'

import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { MediaFile } from '@/types/media'

interface FileConverterDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  file: MediaFile | null
  onConvert: (fileId: string, targetFormat: string) => void
}

export const FileConverterDialog = ({
  open,
  onOpenChange,
  file,
  onConvert,
}: FileConverterDialogProps) => {
  const [targetFormat, setTargetFormat] = useState('')

  const convertMutation = useMutation({
    mutationFn: ({ id, targetFormat }: { id: string; targetFormat: string }) =>
      MediaApi.convertFile({ id, body: { target_format: targetFormat } }),
    onSuccess: () => {
      onConvert(file!.id, targetFormat)
      onOpenChange(false)
    },
  })

  if (!file) return null

  const getAvailableFormats = () => {
    if (file.type === 'image') {
      return ['jpg', 'png', 'webp', 'gif', 'tiff']
    } else if (file.type === 'document') {
      return ['pdf', 'docx', 'txt']
    } else if (file.type === 'video') {
      return ['mp4', 'webm', 'mov']
    }
    return []
  }

  const handleConvert = () => {
    convertMutation.mutate({
      id: file.id,
      targetFormat,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Convert file</DialogTitle>
          <DialogDescription>
            Convert <span className="font-medium text-ink">{file.filename}</span> to another format.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="space-y-1.5">
            <Label>Target format</Label>
            <Select
              value={targetFormat}
              onValueChange={setTargetFormat}
              disabled={convertMutation.isPending}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select format" />
              </SelectTrigger>
              <SelectContent>
                {getAvailableFormats().map((fmt) => (
                  <SelectItem key={fmt} value={fmt} disabled={file.filename.endsWith(fmt)}>
                    {fmt.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {convertMutation.isPending && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-text-2">
                <span>Converting…</span>
              </div>
              <Progress value={undefined} className="animate-pulse" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={convertMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConvert}
            disabled={!targetFormat || convertMutation.isPending}
          >
            <FileType />
            {convertMutation.isPending ? 'Converting…' : 'Convert'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
